import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { theme } from '../../../theme/theme'
import { ROUTES } from '../../../constants/routes'
import { CertificationStepLayout } from '../../../components/certification/CertificationStepLayout'
import { PolicyDocumentUpload } from '../../../components/certification/PolicyDocumentUpload'
import { OnClickButton } from '../../../components/buttons/onclick/OnClickButton'
import { BodyLarge, BodySmallMedium } from '../../../components/styles/Typography'
import { Arrow } from '../../../components/icons'
import {
  useSelectCertificationPolicyAnswer,
  useSelectCertificationPolicyDocuments,
  useSelectCertificationScreeningId,
} from '../../../state/selectors'
import { useCertificationStore } from '../../../state/store'
import { useGoToPreviousQuestion } from '../../../components/screening/useGoToPreviousQuestion'
import { shouldOfferPolicyDocumentUpload } from '../../../utils/certification'

export const CertificationUpload = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const screeningId = useSelectCertificationScreeningId()
  const policyAnswer = useSelectCertificationPolicyAnswer()
  const documents = useSelectCertificationPolicyDocuments()
  const setPolicyDocuments = useCertificationStore((state) => state.setPolicyDocuments)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const goToPreviousQuestion = useGoToPreviousQuestion({
    screeningId,
    questionsPath: ROUTES.ADMIN_CERTIFICATION_QUESTIONS,
    fallbackMessage: t('admin.certification.previousQuestionError'),
    onError: setErrorMessage,
  })

  const goToSubmit = () => navigate(ROUTES.ADMIN_CERTIFICATION_SUBMIT)

  const skipUpload = () => {
    setPolicyDocuments([])
    goToSubmit()
  }

  if (!screeningId) return <Navigate to={ROUTES.ADMIN_CERTIFICATION} replace />
  if (!shouldOfferPolicyDocumentUpload(policyAnswer)) return <Navigate to={ROUTES.ADMIN_CERTIFICATION_SUBMIT} replace />

  return (
    <CertificationStepLayout
      title={t('admin.certification.uploadTitle')}
      goBack={goToPreviousQuestion}
      errorMessage={errorMessage}
      primaryAction={
        <OnClickButton
          text={t('common.form.continue')}
          onClick={goToSubmit}
          icon={<Arrow color={theme.color.white} size={15} />}
          disabled={documents.length === 0}
        />
      }
      secondaryAction={
        <SkipUpload type="button" onClick={skipUpload}>
          {t('common.form.skip')}
        </SkipUpload>
      }
    >
      <BodyLarge>{t('admin.certification.uploadDescription')}</BodyLarge>
      <PolicyDocumentUpload documents={documents} onChange={setPolicyDocuments} />
    </CertificationStepLayout>
  )
}

const SkipUpload = styled(BodySmallMedium).attrs({ as: 'button' })`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.color.plumMid};

  &:hover {
    text-decoration: underline;
  }
`
